export const layer2Template = `# Layer 2：Marveland 前台完整實現

> 目標：基於 Layer 1 的骨架，生成前台所有頁面與組件的**完整 UI 與業務邏輯**。
> **禁止**：修改型別定義、修改 Worker API、新增路由、修改部署配置。
> 輸入：Layer 1 已完成的專案結構與型別。
> 輸出要求：前台所有頁面可正常渲染、篩選正確運作、表單可提交。

---

## 前置條件

Layer 1 已完成：
- ✅ 專案結構、package.json、tsconfig
- ✅ 型別定義（\`types/cms.ts\` 與 Worker 版本一致）
- ✅ Worker API 骨架（路由存在、auth 可用）
- ✅ defaultCMSData 包含完整品牌資料
- ✅ React Router 骨架 + Layout（Header/Footer placeholder）

---

## 1. CMSContext 完整實現

\`apps/web/src/context/CMSContext.tsx\`：
- 使用 \`createContext\` + \`useReducer\` 或 \`useState\`
- 初始化載入 \`defaultCMSData\`（從 \`apps/cms-worker/src/data/default.ts\` 同步的靜態副本，或從 \`data/cms.json\` 載入）
- mount 後 \`fetch(\\\`\${import.meta.env.VITE_CMS_API_URL}/api/cms/data\\\`)\` 更新資料
- 提供：
  - \`cmsData: CMSData\`
  - \`loading: boolean\`
  - \`updateCMSData(partial: Partial<CMSData>)\` — 調用 Worker POST /api/cms/data
  - \`submitInquiry(inquiry: Omit<Inquiry, 'id' | 'createdAt' | 'status'>)\` — 調用 POST /api/inquiries
  - \`getAgent(id: string): Agent | undefined\`
  - \`getProperty(id: string): Property | undefined\`

---

## 2. 頁面路由（已在 Layer 1 建立，現填充內容）

\`\`\`
/              → Home（Hero + Features + About + PropertyGrid + News + Contact）
/about         → About（公司簡介 + 業務範圍 + 團隊）
/properties    → Properties（篩選 + 物業列表）
/properties/:id → PropertyDetail（圖片 + 資訊 + 聯絡）
/owner-listing → OwnerListing（動態表單）
/contact       → ContactPage（聯絡資訊 + 地圖）
/admin         → Admin（後台，見 Layer 3）
\`\`\`

---

## 3. Layout 組件

### Header
- 固定定位，滾動後背景變白（\`bg-white/95 backdrop-blur\`）
- 手機版：漢堡選單（Framer Motion 展開動畫）
- Logo：手機 48px，桌面 60px
- 導航項目從 \`cmsData.nav\` 渲染
- **不可**顯示 \`/admin\` 連結

### Footer
- 三欄布局（桌面）/ 單欄堆疊（手機）
- 顯示公司名稱、牌照號碼（C-076840）、版權
- 底部加入「Powered by JKDCoding」連結至 \`https://jkdcoding.com/\`
- **不可**出現後台管理連結

---

## 4. 首頁區塊組件

### Hero.tsx
- 全寬背景圖（\`cmsData.hero.backgroundImage\`）
- 標題兩行（\`titleLine1\`, \`titleLine2\`）
- 副標題 + 描述
- 兩個 CTA 按鈕（primary/secondary）
- 手機版文字縮小、按鈕堆疊

### Features.tsx（服務區塊）
- 從 \`cmsData.services.items\` 渲染卡片
- 每個卡片：Lucide icon + 標題 + 描述
- 3 欄桌面 / 1 欄手機

### AboutSection.tsx
- 左圖右文（桌面）/ 上下堆疊（手機）
- 顯示公司名稱、成立年份、描述
- 業務範圍以 tag 形式顯示

### PropertyGrid.tsx
- 篩選條件必須顯示 label：
  - 租售類型（sale / rent / sale_or_rent）
  - 地區（tsuen_wan / kwai_chung / lai_chi_kok / kowloon）
  - 物業類型（office / industrial / shop / car_park）
  - 售價範圍（下拉選項）
  - 租金範圍（下拉選項）
  - 關鍵字輸入
- 結果數量顯示（「找到 X 個物業」）
- 平滑滾動到結果區
- 無結果時顯示友善提示

### PropertyCard.tsx
- 封面圖（第一張，object-cover）
- 標籤（\`tags\` 陣列，小 badge）
- 價錢（格式化顯示，如「HK$ 5,000,000」）
- 地區 + 物業類型
- 地圖按鈕（開啟 MapModal）
- 詳情連結（\`/properties/:id\`）
- WhatsApp 聯絡按鈕（連結到負責人，使用 \`getAgent(property.agentId)\`）
- 懸停效果（陰影、圖片縮放）

### NewsSection.tsx
- 從 \`cmsData.news\` 渲染（最多顯示 3 則）
- 每則：圖片 + 日期 + 標題 + 摘要
- 「查看更多」連結

### Contact.tsx
- 電話（可點擊撥號）
- WhatsApp（按鈕連結，多個號碼分行顯示）
- 地址（點擊彈出 MapModal）
- 電郵（mailto 連結）
- 服務時間

---

## 5. 其他頁面

### About.tsx
- 公司簡介完整文字
- 業務範圍列表
- 銷售團隊（從 \`cmsData.agents\` 渲染，顯示照片、姓名、職稱、WhatsApp）

### Properties.tsx
- 重用 \`PropertyGrid\` 組件
- 頁面標題 + 描述

### PropertyDetail.tsx
- 圖片 gallery（點擊放大，左右切換）
- 資訊卡（價錢、面積、類型、地區、地址、標籤、特色）
- 負責人卡片（照片、姓名、職稱、WhatsApp 按鈕）
- Google Maps iframe（從 \`mapLink\` 或地址生成）
- 平面圖（如有）
- 返回列表按鈕
- 相關物業推薦（同地區同類型）

### OwnerListing.tsx
- 動態表單：欄位由 \`cmsData.ownerForm.fields\` 定義
- 支援類型：text / tel / email / select / textarea / number
- select 欄位從 \`options\` 渲染
- 表單驗證（required 欄位）
- 提交成功後顯示感謝頁
- 提交時呼叫 \`submitInquiry({ type: 'owner_listing', ... })\`

### ContactPage.tsx
- 重用 \`Contact\` 組件
- 可附加聯絡表單（name, phone, email, message）
- 提交時呼叫 \`submitInquiry({ type: 'contact', ... })\`

---

## 6. 共用組件

### MapModal.tsx
- Google Maps iframe popup（Dialog/Modal）
- 地址使用 \`encodeURIComponent\` 編碼
- 外部開啟按鈕（在新視窗開啟 Google Maps）
- 關閉按鈕

### WhatsAppFloat.tsx
- 右下角綠色圓形按鈕（固定定位）
- 使用主要 WhatsApp 號碼（第一個）
- **不在 \`/admin\` 頁面顯示**
- 手機版大小適當（不遮擋內容）

---

## 7. 工具函數

### utils/propertyFilters.ts
實作 \`filterProperties(properties, filters)\`：

\`\`\`ts
interface Filters {
  listingType?: ListingType | 'all';
  district?: District | 'all';
  category?: PropertyCategory | 'all';
  priceRange?: { min?: number; max?: number };
  rentRange?: { min?: number; max?: number };
  keyword?: string;
}
\`\`\`

規則：
- 售價篩選只對 \`sale\` / \`sale_or_rent\` 生效
- 租金篩選只對 \`rent\` / \`sale_or_rent\`（且 \`rentPrice\` 存在）生效
- 關鍵字比對 \`title\`、\`location\`、\`address\`、\`code\`、\`tags\`（不區分大小寫）
- 多個篩選條件為 AND 關係

### lib/imageCompression.ts
實作 \`smartCompressImage(file: File): Promise<File>\`：
- ≤ 300KB / SVG / GIF：不壓縮，直接回傳
- 300KB ~ 2MB：maxWidth 2048，品質 0.75
- 2MB ~ 5MB：maxWidth 1920，品質 0.65
- > 5MB：maxWidth 1600，品質 0.55
- 輸出格式：統一轉 WebP
- 壓縮後若檔案沒變小則回退原檔
- 使用 Canvas API 實作

---

## 8. AnalyticsContext.tsx

- 使用 \`useLocation()\` 追蹤 pageview
- 使用 \`sessionStorage\` 儲存 \`marveland_analytics_session\`（UUID v4）
- 從 URL 讀取 \`utm_source\` / \`utm_medium\` / \`utm_campaign\` / \`utm_content\`
- 提供 \`trackEvent(meta: Record<string, any>)\` 發送 conversion
- 自動追蹤點：
  - 每個 route change → \`pageview\`
  - 物業卡片 WhatsApp 按鈕：\`trackEvent({ action: 'whatsapp_contact', propertyId, agentId })\`
  - 聯絡我們 WhatsApp：\`trackEvent({ action: 'whatsapp_contact', page: 'contact' })\`
  - 業主放盤提交：\`trackEvent({ action: 'owner_listing_submit' })\`
- 資料發送至 \`POST \${VITE_CMS_API_URL}/api/analytics/event\`

---

## 9. 生成完成後的驗證步驟

1. \`npm run build\` 成功（無 TypeScript 錯誤）
2. Header 滾動時背景變白
3. Footer 顯示牌照號碼與 JKDCoding 連結
4. 物業篩選：選「出售」時只顯示 sale / sale_or_rent
5. 物業篩選：選「出租」時只顯示 rent / sale_or_rent（有 rentPrice）
6. 關鍵字搜尋可匹配 title、location、address、code、tags
7. PropertyCard 的 WhatsApp 按鈕連結正確（去掉非數字）
8. PropertyDetail 的 Google Maps 使用 \`encodeURIComponent\`
9. OwnerListing 表單欄位與 \`cmsData.ownerForm.fields\` 一致
10. WhatsAppFloat 在 \`/admin\` 不顯示
11. Analytics 使用 \`sessionStorage\` 而非 \`localStorage\`
12. 圖片壓縮函數可正確處理各種大小檔案

---

## 10. 常見陷阱（Layer 2 特別關注）

1. **WhatsApp 號碼格式**：儲存保留 \`852\`，連結時 \`replace(/\\D/g, '')\`
2. **地址編碼**：Google Maps 連結使用 \`encodeURIComponent\`
3. **Footer 連結**：不要顯示 \`/admin\`
4. **篩選邏輯**：\`sale_or_rent\` 要同時考慮 price 和 rentPrice
5. **Analytics session**：使用 \`sessionStorage\`
6. **MediaPicker 單/多選**：封面圖用 single，物業相簿用 multiple
7. **Framer Motion**：確保 import 正確，手機版動畫不卡頓
`;
